import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	OnChanges,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { TranslateService } from '@ngx-translate/core';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { delay, interval, take } from 'rxjs';
import { NgxCommentService } from '@go-modules/services/comment/comment.service';
import type { Comment, CommentReaction } from '@go-modules/interfaces/comments/comment';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { SelectedService } from 'go-modules/services/selected/selected.service';

export enum MODES {
	DISPLAY = 'display',
	ADD = 'add',
	FEEDBACK = 'feedback'
}

interface Reaction {
	emoji: string;
	count: number;
	i_reacted: boolean;
	variants: {
		emoji: string;
		count: number;
		reactors: string[];
		i_reacted: boolean;
	}[];
}

export const SKIN_TONE_KEY = 'emoji-mart.skin';

@Component({
	selector: 'ngx-comment-reactions',
	templateUrl: './comment-reactions.component.html',
	styleUrls: ['./comment-reactions.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class NgxCommentReactionsComponent implements OnInit, OnChanges {
	@Input() public comment: Comment;
	@Input() public mode: MODES;
	@Output() public setSelectedComment?: EventEmitter<void> = new EventEmitter<void>();
	@ViewChild(MatMenuTrigger) private emojiMenuTrigger: MatMenuTrigger;
	public reactions: CommentReaction[] = [];
	public uniqueReactions: Reaction[] = [];
	public modes = MODES;
	public emojiFilter;

	constructor (
		private cdr: ChangeDetectorRef,
		private commentService: NgxCommentService,
		private translate: TranslateService,
		private commentListService: CommentListService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(selectedServiceToken) private selectedService: SelectedService
	) {}

	public ngOnInit () {
		this.emojiFilter = this.commentListService.emojiFilter;
		this.setReactions(this.comment);
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if ('comment' in changes && changes.comment.currentValue != null) {
			this.setReactions(changes.comment.currentValue);
		}
	}

	public focusSearchField (event) {
		event.stopPropagation();
		if (this.setSelectedComment) {
			this.setSelectedComment.emit();
		}
		interval(100)
			.pipe(
				take(1),
				delay(1)
			).subscribe(() => {
				const searchField: HTMLElement = document.querySelector('div.emoji-mart-search input[type="search"]');
				searchField?.focus();
			});
	}

	// TODO simplify this with ngx-translate pluralization STAB-578
	public getTooltip (reaction) {
		if (this.mode === MODES.FEEDBACK) {
			if (this.getBaseEmoji(reaction.emoji) === '👍') {
				return this.translate.instant('feedback-panel-ai-feedback-good');
			}
			return this.translate.instant('feedback-panel-ai-feedback-bad');
		}
		let tooltip = '';
		reaction.variants.forEach((variant) => {
			if (variant.i_reacted) {
				if (variant.reactors.length > 1) {
					const names = [...variant.reactors];
					const lastName = names.pop();
					tooltip += this.translate.instant('feedback-panel-you-and-many-other-reactors', {
						names: names.join(', '),
						lastName,
						emoji: variant.emoji
					});
				} else if (variant.reactors.length === 1) {
					tooltip += this.translate.instant('feedback-panel-you-and-one-other-reactor', {
						name: variant.reactors[0],
						emoji: variant.emoji
					});
				} else {
					tooltip += this.translate.instant('feedback-panel-just-you-reactor', {
						emoji: variant.emoji
					});
				}
				tooltip += '\n';
			} else {
				if (variant.reactors.length > 2) {
					const names = [...variant.reactors];
					const lastName = names.pop();
					tooltip += this.translate.instant('feedback-panel-many-other-reactors', {
						names: names.join(', '),
						lastName,
						emoji: variant.emoji
					});
				} else if (variant.reactors.length === 2) {
					tooltip += this.translate.instant('feedback-panel-two-other-reactors', {
						nameOne: variant.reactors[0],
						nameTwo: variant.reactors[1],
						emoji: variant.emoji
					});
				} else {
					tooltip += this.translate.instant('feedback-panel-one-other-reactor', {
						name: variant.reactors[0],
						emoji: variant.emoji
					});
				}
				tooltip += '\n';
			}
		});
		return tooltip;
	}

	public toggleReaction (event, emoji) {
		event.stopPropagation();
		if (this.setSelectedComment) {
			this.setSelectedComment.emit();
		}
		const alreadyAdded = this.checkIfAlreadyAdded(emoji);
		if (alreadyAdded) {
			this.removeReaction(emoji, alreadyAdded.id);
		} else {
			const withSkinTone = this.addSkinTone(this.getBaseEmoji(emoji));
			this.addEmoji(withSkinTone);
		}
	}

	public addReaction (event) {
		if (this.checkIfAlreadyAdded(event.emoji.native)) {
			return this.emojiMenuTrigger.closeMenu();
		} else {
			this.addEmoji(event.emoji.native);
			this.emojiMenuTrigger.closeMenu();
		}
	}

	private removeReaction (emoji, reactionId) {
		this.commentService.deleteReaction(this.comment.comment_id, reactionId)
			.subscribe(() => {
				const uniqueReaction = this.uniqueReactions.find(((reaction) => {
					const baseEmoji = this.getBaseEmoji(reaction.emoji);
					return baseEmoji === emoji;
				}));
				uniqueReaction.count--;
				if (uniqueReaction.count === 0 && this.mode !== MODES.FEEDBACK) {
					const uniqueIndex = this.uniqueReactions.findIndex(((reaction) => {
						const baseEmoji = this.getBaseEmoji(reaction.emoji);
						return baseEmoji === emoji;
					}));
					this.uniqueReactions.splice(uniqueIndex, 1);
				} else {
					uniqueReaction.i_reacted = false;
					const variant = uniqueReaction.variants.find((variant) => variant.i_reacted);
					variant.count--;
					if (this.mode !== MODES.FEEDBACK && variant.count === 0) {
						uniqueReaction.variants.splice(uniqueReaction.variants.indexOf(variant), 1);
					} else {
						variant.i_reacted = false;
					}
				}
				const reactionIndex = this.reactions.findIndex(((reaction) => {
					const baseEmoji = this.getBaseEmoji(reaction.emoji);
					return baseEmoji === emoji &&
						reaction.created_by_user_id === this.userService.currentUser.user_id;
				}));
				this.reactions.splice(reactionIndex, 1);
				this.commentListService.editComment(this.comment, {reactions: this.reactions});
				this.cdr.detectChanges();
			});
	}

	private addEmoji (emoji) {
		this.commentService.addReaction(this.comment.comment_id, {emoji})
			.subscribe((res: CommentReaction) => {
				if (!this.reactions) {
					this.reactions = [res];
				} else {
					this.reactions.push(res);
				}
				if (this.mode === MODES.ADD || this.mode === MODES.FEEDBACK) {
					this.commentListService.editComment(this.comment, {reactions: this.reactions});
				} else {
					const baseEmoji = this.getBaseEmoji(res.emoji);
					const objIndex = this.uniqueReactions.findIndex(((reaction) => reaction.emoji === baseEmoji));
					if (objIndex >= 0) {
						this.uniqueReactions[objIndex].count++;
						this.uniqueReactions[objIndex].i_reacted = true;
						const variant = this.uniqueReactions[objIndex].variants
							.find((variant) => variant.emoji === res.emoji);
						if (variant) {
							variant.count++;
							variant.i_reacted = true;
						} else {
							this.uniqueReactions[objIndex].variants.push({
								emoji: res.emoji, reactors: [], count: 1, i_reacted: true
							});
						}
					} else {
						this.uniqueReactions.push({
							emoji: this.getBaseEmoji(res.emoji),
							count: 1,
							i_reacted: true,
							variants: [{emoji: res.emoji, reactors: [], count: 1, i_reacted: true}]
						});
					}
					this.commentListService.editComment(this.comment, {reactions: this.reactions});
					this.cdr.detectChanges();
				}
				if (this.comment.was_ai_generated && this.getBaseEmoji(res.emoji) === '👎' && this.comment.children.length === 0 && this.selectedService.getGroup().hasInstructorRole(true)) {
					this.commentListService.removeComment(this.comment);
					const session = this.selectedService.getSession();
					this.commentService.deleteComment(session.session_id, this.comment.comment_id).subscribe();
				}
			});
	}

	public handleKeyDownEmojiPopup ($event) {
		if ($event.key === 'Escape') {
			this.emojiMenuTrigger.closeMenu();
		} else {
			$event.stopPropagation();
		}
	}

	private checkIfAlreadyAdded (emoji) {
		if (!this.reactions) {
			return null;
		}
		return this.reactions.find((reaction) => {
			return this.getBaseEmoji(reaction.emoji) === this.getBaseEmoji(emoji) &&
				reaction.created_by_user_id === this.userService.currentUser.user_id;
		});
	}

	private setUniqueReactions (reactions) {
		this.uniqueReactions = Object.values(reactions.reduce((obj, item) => {
			const baseEmoji = this.getBaseEmoji(item.emoji);
			obj[baseEmoji] = obj[baseEmoji] ||
				{
					emoji: baseEmoji,
					variants: [],
					count: 0,
					i_reacted: false
				};
			if (!obj[baseEmoji].variants.some((variant) => variant.emoji === item.emoji)) {
				obj[baseEmoji].variants.push({emoji: item.emoji, reactors: [], count: 0, i_reacted: false});
			}
			obj[baseEmoji].count++;
			const variant = obj[baseEmoji].variants.find((variant) => variant.emoji === item.emoji);
			variant.count++;
			if (item.created_by_user_id === this.userService.currentUser.user_id) {
				obj[baseEmoji].i_reacted = true;
				variant.i_reacted = true;
			} else {
				obj[baseEmoji].variants.find((variant) => variant.emoji === item.emoji)
					.reactors.push(item.created_by_user_name);
			}
			return obj;
		}, {}));

		if (this.mode === MODES.FEEDBACK) {
			const thumbsUpExists = this.uniqueReactions.some((reaction) => this.getBaseEmoji(reaction.emoji) === '👍');
			if (!thumbsUpExists) {
				this.uniqueReactions.unshift({emoji: '👍', count: 0, i_reacted: false, variants: [
					{emoji: '👍', reactors: [], count: 0, i_reacted: false}
				]});
			}
			const thumbsDownExists = this.uniqueReactions.some((reaction) => this.getBaseEmoji(reaction.emoji) === '👎');
			if (!thumbsDownExists) {
				this.uniqueReactions.push({emoji: '👎', count: 0, i_reacted: false, variants: [
					{emoji: '👎', reactors: [], count: 0, i_reacted: false}
				]});
			}
			// thumbs up should always be first
			this.uniqueReactions.sort((a) => {
				if (a.emoji === '👍') {
					return -1;
				}
				return 1;
			});
		}
	}

	private getBaseEmoji (emoji) {
		// remove 5 different skin tones from emoji (as listed below in addSkinTone)
		// so we have a base emoji for comparison
		return emoji.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');
	}

	private addSkinTone (emoji) {
		const preferredSkinTone = localStorage.getItem(SKIN_TONE_KEY);
		if (preferredSkinTone) {
			const utfs = {
				1: '',
				2: '\u{1F3FB}',
				3: '\u{1F3FC}',
				4: '\u{1F3FD}',
				5: '\u{1F3FE}',
				6: '\u{1F3FF}'
			};
			emoji = emoji + utfs[preferredSkinTone];
		}
		return emoji;
	}

	private setReactions (comment) {
		this.reactions = structuredClone(comment.reactions);
		if (this.reactions || this.mode === MODES.FEEDBACK) {
			this.setUniqueReactions(this.reactions);
		}
	}
}
